'use client';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MobileStock } from './mobile-stock';
import { Quantity } from './quantity';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

export function MobileTable({ variants }: { variants: VariantsEntity[] }) {
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const setSelectedVariant = useVariantsStore(state => state.setSelectedVariant);
  const { t } = useTranslations();
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);

  const sortedVariants = variants.map(variant => ({
    ...variant,
    values: Array.isArray(variant.values)
      ? [...variant.values].sort((a, b) => a.attribute_position - b.attribute_position)
      : [],
  }));

  return (
    <div className="flex w-full flex-col sm:hidden">
      {sortedVariants.map((variant, index) => {
        const priceDisplay = getProductPriceDisplay(variant, { isWholesaler });
        const dynamicAttributes = Array.isArray(variant.values)
          ? variant.values.slice(0, 4).map((value, idx) => ({
              name: value.attribute_name,
              value: value.name,
            }))
          : [];

        const dimensions = [variant.width, variant.height, variant.length]
          .filter(dim => dim !== undefined && dim !== null && dim !== '')
          .map(dim => String(dim))
          .join('x');

        const data = [
          {
            name: null,
            value: variant.name || variant.code,
          },
          {
            name: `${t('text1462')} (cm)`,
            value: dimensions || '--',
          },
          ...dynamicAttributes,
          {
            name: t('text164'),
            value: priceDisplay.priceOnRequest
              ? t('text533')
              : formatPrice(priceDisplay.currentWithVat, currency, currencySettings),
          },
          {
            name: null,
            value: <MobileStock variant={variant} name={t('text3605')} />,
          },
        ];

        return (
          <div
            key={variant.id}
            className={cn(
              'flex w-full flex-col items-start justify-start gap-2 border-b border-b-[#E9E9E9] px-2 pb-6 pt-6',
            )}
          >
            {data.map((attribute, idx) => (
              <div
                key={idx}
                className={cn(
                  'flex w-full items-center justify-between',
                  idx === 0 && 'justify-start',
                )}
              >
                {attribute.name !== null && (
                  <span className={cn('text-sm font-medium text-foreground')}>
                    {attribute.name}
                  </span>
                )}
                <p
                  className={cn(
                    'text-sm font-normal text-foreground',
                    idx === 0 && 'font-normal text-primary underline',
                    attribute.name === null && 'w-full',
                    idx === 0 && 'cursor-pointer',
                    idx === 0 && selectedVariant?.id === variant.id && 'font-bold',
                  )}
                  onClick={() => {
                    if (idx === 0) {
                      setSelectedVariant(variant);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  {attribute.value}
                </p>
              </div>
            ))}
            <Quantity className="mt-2 w-full justify-between" variantId={variant.id} />
          </div>
        );
      })}
    </div>
  );
}
